// src/core/layout-manager.js
//
// LayoutManager
// -------------
// Registers DSL layouts and selects the correct one
// based on slice length or weighted random fallback.
//
// NOTE:
// Rendering is no longer done here — slideshow-view
// handles <img>/<video> rendering directly.

import { layoutDSL } from "core/layout-dsl.js";

export class LayoutManager {
  constructor() {
    this.layouts = [];
    this._registerDefaults();
  }

  // ------------------------------------------------------------
  // Default layouts
  // ------------------------------------------------------------
  _registerDefaults() {
    // ------------------------------------------------------------
    // Default layouts (STRICT JSON)
    // ------------------------------------------------------------

    // 1. Single
    this.registerDSL(
      "single",
      "Single",
      `
    grid:
      cols: 1
      rows: 1

    areas:
      A

    slots:
      A: { "index": 0 }

    weight:
      weight: 3
  `,
    );

    // 2. Two side-by-side
    this.registerDSL(
      "two-side",
      "Two Side By Side",
      `
    grid:
      cols: 2
      rows: 1

    areas:
      A B

    slots:
      A: { "index": 0 }
      B: { "index": 1 }

    weight:
      weight: 2
  `,
    );

    // 3. One large + two small
    this.registerDSL(
      "one-plus-two",
      "One Large + Two Small",
      `
    grid:
      cols: 2
      rows: 2

    areas:
      A B
      A C

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }

    weight:
      weight: 2
  `,
    );

    // 3. One large + four small
    this.registerDSL(
      "one-plus-four",
      "One Large + Four Small",
      `
    grid:
      cols: 3
      rows: 2

    areas:
      A B C
      A D E

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }
      D: { "index": 3 }
      E: { "index": 4 }

    weight:
      weight: 2
  `,
    );

    // 3. Two large + two small
    this.registerDSL(
      "two-plus-four",
      "Two large + Two Small",
      `
    grid:
      cols: 3
      rows: 2

    areas:
      A B C
      A B D

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }
      D: { "index": 3 }

    weight:
      weight: 2
  `,
    );

    // 3. Large + Two Small + Large
    this.registerDSL(
      "large-plus-two-small-plus-large",
      "Large + Two Small + Large",
      `
    grid:
      cols: 3
      rows: 2

    areas:
      A C B
      A D B

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }
      D: { "index": 3 }

    weight:
      weight: 2
  `,
    );

    // 4. Four grid
    this.registerDSL(
      "four-grid",
      "Four grid",
      `
    grid:
      cols: 2
      rows: 2

    areas:
      A B
      C D

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }
      D: { "index": 3 }

    weight:
      weight: 1
  `,
    );

    // ------------------------------------------------------------
    // NEW LAYOUTS
    // ------------------------------------------------------------

    // 5. 3×2 grid (6 images)
    this.registerDSL(
      "three-by-two",
      "3×2 Grid",
      `
    grid:
      cols: 3
      rows: 2

    areas:
      A B C
      D E F

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }
      D: { "index": 3 }
      E: { "index": 4 }
      F: { "index": 5 }

    weight:
      weight: 1
  `,
    );

    // 6. 4×2 grid (8 images)
    this.registerDSL(
      "four-by-two",
      "4×2 Grid",
      `
    grid:
      cols: 4
      rows: 2

    areas:
      A B C D
      E F G H

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }
      D: { "index": 3 }
      E: { "index": 4 }
      F: { "index": 5 }
      G: { "index": 6 }
      H: { "index": 7 }

    weight:
      weight: 1
  `,
    );

    // 7. Mosaic (asymmetric, visually interesting)
    this.registerDSL(
      "mosaic",
      "Mosaic",
      `
    grid:
      cols: 3
      rows: 3

    areas:
      A A B
      A C C
      D E F

    slots:
      A: { "index": 0 }
      B: { "index": 1 }
      C: { "index": 2 }
      D: { "index": 3 }
      E: { "index": 4 }
      F: { "index": 5 }

    weight:
      weight: 1
  `,
    );
  }

  // ------------------------------------------------------------
  // Registration
  // ------------------------------------------------------------
  register(layout) {
    this.layouts.push(layout);
  }

  registerDSL(id, name, dslString) {
    const parsed = layoutDSL`${dslString}`;
    this.register({
      id,
      name,
      weight: parsed.weight ?? 1,
      dsl: parsed,
    });
  }

  // ------------------------------------------------------------
  // Lookup helpers
  // ------------------------------------------------------------
  getById(id) {
    return this.layouts.find((l) => l.id === id) || this.layouts[0];
  }

  getName(id) {
    return this.getById(id).name;
  }

  nextLayoutId(currentId) {
    const idx = this.layouts.findIndex((l) => l.id === currentId);
    if (idx === -1) return this.layouts[0].id;
    return this.layouts[(idx + 1) % this.layouts.length].id;
  }

  previousLayoutId(currentId) {
    const idx = this.layouts.findIndex((l) => l.id === currentId);
    if (idx === -1) return this.layouts[0].id;
    return this.layouts[(idx - 1 + this.layouts.length) % this.layouts.length]
      .id;
  }

  getNumSlots(layoutId) {
    const layout = this.getById(layoutId);
    return Object.keys(layout.dsl.slots || {}).length;
  }

  // ------------------------------------------------------------
  // Weighted random selection
  // ------------------------------------------------------------
  pickWeightedRandom() {
    const total = this.layouts.reduce((sum, l) => sum + (l.weight ?? 1), 0);

    let r = Math.random() * total;

    for (const layout of this.layouts) {
      r -= layout.weight ?? 1;
      if (r <= 0) return layout.id;
    }

    return this.layouts[0].id;
  }
}
